import React, { useState } from "react";
import { Envelope, CheckCircle, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

// Homepage Daily Devotional opt-in card. One verse, one short
// AI-grounded reflection, every morning at 7am ET. The subscriber list
// lives in MongoDB (`devotional_subscribers`) and is the highest-
// leverage growth tool on the app — daily inbox touch + a re-entry
// CTA back into Holy Words. Independent of the existing user table,
// so visitors can subscribe without creating an account.
export default function DailyDevotionalSubscribe() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/devotional/subscribe", { email: trimmed, name: name.trim() });
      setDone(true);
      toast.success("You're in! First devotional lands tomorrow at 7am.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't subscribe. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Couldn't subscribe.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="devotional-subscribed-card"
        className="rounded-3xl p-7 md:p-10 bg-gradient-to-br from-forest/95 via-forest to-terracotta/70 text-cream shadow-2xl relative overflow-hidden"
      >
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-cream/10 blur-3xl" />
        <div className="relative flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-full bg-cream text-forest grid place-items-center shadow-lg">
            <CheckCircle size={28} weight="fill" />
          </div>
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-200">Subscribed</span>
          <h3 className="font-heading text-3xl">Your inbox is about to get holier.</h3>
          <p className="text-cream/85 max-w-md text-sm">
            Tomorrow at 7am you'll get one verse plus a short reflection — built to be read with your morning coffee.
            See you then.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="devotional-subscribe-form"
      className="rounded-3xl p-7 md:p-10 bg-gradient-to-br from-forest/95 via-forest to-terracotta/55 text-cream shadow-2xl relative overflow-hidden"
    >
      <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-cream/10 blur-3xl" />
      <div className="absolute -left-8 -bottom-12 w-40 h-40 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="relative grid md:grid-cols-2 gap-6 md:gap-10 items-center">
        <div>
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-200 inline-flex items-center gap-1.5">
            <BookOpen size={11} weight="fill" /> Daily devotional
          </span>
          <h3 className="font-heading text-3xl md:text-4xl mt-2 leading-tight">
            One verse, one reflection, every morning.
          </h3>
          <p className="text-cream/85 mt-2 text-sm md:text-base max-w-md">
            Free. No noise. Just scripture and a short word to carry you into the day — written fresh for you each morning.
          </p>
        </div>
        <div className="flex flex-col gap-2.5">
          <label className="block">
            <span className="sr-only">Your first name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="First name (optional)"
              data-testid="devotional-name-input"
              className="w-full h-12 px-4 rounded-full bg-cream/15 text-cream placeholder:text-cream/55 border border-cream/25 focus:bg-cream/25 focus:outline-none focus:border-amber-200 transition-colors"
            />
          </label>
          <label className="block relative">
            <span className="sr-only">Your email</span>
            <Envelope size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/60" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@email.com"
              data-testid="devotional-email-input"
              className="w-full h-12 pl-11 pr-4 rounded-full bg-cream/15 text-cream placeholder:text-cream/55 border border-cream/25 focus:bg-cream/25 focus:outline-none focus:border-amber-200 transition-colors"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="devotional-subscribe-button"
            className="h-12 rounded-full bg-cream text-forest font-bold text-xs tracking-[0.2em] uppercase shadow-xl hover:bg-white hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Sending you the first one..." : "Send me daily verses →"}
          </button>
          <p className="text-cream/55 text-[10px] tracking-wider uppercase text-center">
            Unsubscribe with one tap anytime
          </p>
        </div>
      </div>
    </form>
  );
}
